/**
 * Generate the indication text in the scroll indicator to scroll.
 * @param {number} number - The number to convert.
 * @param {boolean} roundedByThousand - True if the number should be rounded (remove values < 1000>).
 */
export const formatToThousandsDelimiter = (
    number: number,
    roundedByThousand: boolean = false,
) => {
    const scrollToProjectsCount = roundedByThousand
        ? Math.floor(number / 1000) * 1000
        : number;

    return new Intl.NumberFormat('en-US', {}).format(scrollToProjectsCount);
};

/**
 * Formats a number with comma separators for thousands.
 * @param number The number to format.
 * @returns A string representation of the number with comma separators.
 */
export function formatNumberWithCommas(number: number): string {
    const [integerPart = '', ...decimalParts] = number.toString().split('.');
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalParts.length > 0
        ? `${formatted}.${decimalParts.join('.')}`
        : formatted;
}

/**
 * Formats a number with comma separators for thousands and optional decimal places.
 * @param number The number to format.
 * @param decimalPlaces The number of decimal places to show (default is 0).
 * @returns A string representation of the number with comma separators and specified decimal places.
 */
export function formatNumberWithCommasAndDecimals(
    number: number,
    decimalPlaces: number = 0,
): string {
    return number.toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
}

/**
 * Rounds a number to the closest thousand or hundred.
 * @param number The number to round.
 * @param toThousand If true, rounds to the nearest thousand; if false, rounds to the nearest hundred.
 * @param roundUp If true, rounds up; if false, rounds down.
 * @returns The rounded number.
 */
export function roundToNearest(
    number: number,
    toThousand: boolean = true,
    roundUp: boolean = false,
): number {
    const factor = toThousand ? 1000 : 100;

    if (number < factor) {
        return number;
    }

    return roundUp
        ? Math.ceil(number / factor) * factor
        : Math.floor(number / factor) * factor;
}

/**
 * Converts a number to a shortened string representation.
 * Returns:
 *  - '0' for numbers less than 1
 *  - Exact number for 1-999
 *  - Rounded to specified decimal places + 'K' for 1,000-999,999
 *  - Rounded to specified decimal places + 'M' for 1,000,000 and above
 *
 * @param num - The number to be shortened.
 * @param decimalPlaces - The number of decimal places to show (default is 1).
 * @returns A string representation of the number.
 */
export const shortenCount = (
    num: number,
    decimalPlaces: number = 1,
): string => {
    if (num < 1) {
        return '0';
    }

    if (num < 1000) {
        return num.toString();
    }

    if (num < 1000000) {
        const thousands = num / 1000;
        // Only show decimal places if the number has a fractional part
        const hasFraction = thousands % 1 !== 0;
        const places = hasFraction ? decimalPlaces : 0;
        return thousands.toFixed(places) + 'K';
    }

    const millions = num / 1000000;
    // Only show decimal places if the number has a fractional part
    const hasFraction = millions % 1 !== 0;
    const places = hasFraction ? decimalPlaces : 0;
    return millions.toFixed(places) + 'M';
};

/**
 * Formats a time duration in seconds into a human-readable string with hours, minutes, and seconds.
 * @param timeSpent The time duration in seconds to format.
 * @returns A formatted string in the format "Xh Ym Zs" where X, Y, Z are the hours, minutes, and seconds respectively.
 */
export const formatTimeSpent = (timeSpent: number) => {
    const hours = Math.floor(timeSpent / 3600);
    const minutes = Math.floor((timeSpent % 3600) / 60);
    return `${formatNumberWithCommas(hours)}h ${formatNumberWithCommas(minutes)}m`;
};

/**
 * Formats a time duration in seconds into a human-readable phrase, e.g.
 * "2 hours 14 minutes", "14 minutes" or "less than a minute". Unlike
 * `formatTimeSpent`, this never collapses sub-hour durations to "0 hours",
 * making it suitable for tooltips and other prose.
 * @param timeSpent The time duration in seconds to format.
 * @returns A human-readable duration phrase.
 */
export const formatTimeSpentLong = (timeSpent: number): string => {
    const hours = Math.floor(timeSpent / 3600);
    const minutes = Math.floor((timeSpent % 3600) / 60);
    const parts: string[] = [];

    if (hours > 0) {
        parts.push(
            `${formatNumberWithCommas(hours)} ${hours === 1 ? 'hour' : 'hours'}`,
        );
    }
    if (minutes > 0) {
        parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }

    return parts.length > 0 ? parts.join(' ') : 'less than a minute';
};
